'use client';
import SortMobile from '@/components/shared/sort/sort-mobile.component';
import ButtonWithIcon from '@/components/shared/Button/button-with-icon/button-with-icon.component';
import ColoredTag from '@/components/shared/colored-tag/colored-tag.component';
import {
    ClearAllTag,
    FilterItemButton,
    FiltersMobile,
} from '@/components/filtersV2';

import { useFilter, useSort, useTranslation } from '@/hook';
import {
    Filter,
    FilterActive,
    SortIcon,
    SortIconActive,
} from '../../../../public/Images';
import React, { useState } from 'react';
import {
    CarStatusText,
    defaultEmiFilter,
    defaultPriceFilter,
    defaultYearFilter,
    FILTER_NAMES,
    getDefaultIClientRetailFilters,
    IClientRetailFilters,
    ISearchTerms,
} from '@/types';
import { AVAILABLE_LANGUAGES } from '@/constants';
import {
    cloneClientRetailFilters,
    formatBrandName,
    formatPriceForDisplay,
    formatYearForDisplay,
} from '@/utils/filterUtil';
import { numberWithCommas } from '@/utils';
import { SORTING_PLANS } from '@/types/filter.types';
import { getCategoryBadgeConfig } from '@/components/car-details/CarProfileNew/constants';

interface TFilterHeaderMobileProps {
    filters: IClientRetailFilters;
    setFilters: (arg: IClientRetailFilters) => void;
    displayedCount: number;
    lang: AVAILABLE_LANGUAGES;
    searchTerms: ISearchTerms;
}

const FilterHeaderMobile = ({
    filters,
    setFilters,
    displayedCount,
    lang,
    searchTerms,
}: TFilterHeaderMobileProps) => {
    const { t } = useTranslation();
    const { sorting_plan: sortingOptions } = useSort();
    const [selectedFilter, setSelectedFilter] = useState<string>('');
    const [open, setOpen] = useState(false);
    const [openSort, setOpenSort] = useState(false);

    const {
        availableTagClicked,
        kilometersTagClicked,
        transmissionTagClicked,
        bodyTypeTagClicked,
        yearTagClicked,
        priceTagClicked,
        discountedTagClicked,
        emiTagClicked,
        makeTagClicked,
        modelTagClicked,
        categoryTagClicked,
        filterObjectNotEmpty,
        localFilters: headerFilters,
        setLocalFilters,
    } = useFilter(searchTerms, cloneClientRetailFilters(filters), setFilters);

    const filterNames = [
        t(FILTER_NAMES.MAKE_MODEL),
        t(FILTER_NAMES.PRICE),
        t(FILTER_NAMES.DISCOUNTED),
        t('Refurbished Status'),
        t(FILTER_NAMES.EMI),
        t(FILTER_NAMES.AVAILABLE),
        t(FILTER_NAMES.MODEL_YEAR),
        t(FILTER_NAMES.KILOMETERS),
        t(FILTER_NAMES.TRANSMISSION),
        t(FILTER_NAMES.BODY_STYLE),
    ];

    const filterMobileButtonClicked = () => {
        setSelectedFilter(filterNames[0]);
        setOpen(true);
    };

    const sortMobileButtonClicked = () => {
        setOpenSort(true);
    };

    const applyFiltersClicked = (f: IClientRetailFilters) => {
        setOpen(false);
        setLocalFilters(cloneClientRetailFilters(f));
    };

    const clearFiltersClicked = () => {
        setOpen(false);
        setLocalFilters(getDefaultIClientRetailFilters());
    };

    const filterMobileItemButtonClicked = (choosenFilter: string) => {
        setSelectedFilter(choosenFilter);
        setOpen(true);
    };

    const badgeConfig =
        headerFilters.category !== 'all' &&
        getCategoryBadgeConfig(headerFilters.category, t);

    return (
        <>
            <div
                className={
                    'w-full flex items-start overflow-y-scroll gap-1 scrollbar-hide'
                }
            >
                <ButtonWithIcon
                    img={Filter.src}
                    imgActive={FilterActive.src}
                    text={t('Filters')}
                    buttonClicked={filterMobileButtonClicked}
                    active={filterObjectNotEmpty}
                    extraClassNames={''}
                    innerExtraClassNames={''}
                />
                <ButtonWithIcon
                    img={SortIcon.src}
                    imgActive={SortIconActive.src}
                    text={t('sortBy')}
                    buttonClicked={sortMobileButtonClicked}
                    active={
                        ![SORTING_PLANS.MOST_RELEVANT].includes(sortingOptions)
                    }
                    extraClassNames={''}
                    innerExtraClassNames={''}
                />
                {filterNames.map(item => (
                    <FilterItemButton
                        key={item}
                        text={item}
                        buttonClicked={filterMobileItemButtonClicked}
                    />
                ))}
            </div>
            <div
                className={`flex items-center justify-start overflow-y-scroll scrollbar-hide  ${
                    filterObjectNotEmpty ? 'mt-3' : ''
                }`}
            >
                {filterObjectNotEmpty && (
                    <ClearAllTag
                        tagClicked={clearFiltersClicked}
                        variant={'grey'}
                    />
                )}
                {headerFilters.price.min !== defaultPriceFilter.min && (
                    <ColoredTag
                        text={formatPriceForDisplay(
                            [
                                Math.max(
                                    headerFilters.price.min,
                                    searchTerms.priceTerms.min
                                ),
                                Math.min(
                                    headerFilters.price.max,
                                    searchTerms.priceTerms.max
                                ),
                            ],
                            lang,
                            t
                        )}
                        tagClicked={priceTagClicked}
                    />
                )}
                {headerFilters.discounted && (
                    <ColoredTag
                        text={'Discounted'}
                        tagClicked={discountedTagClicked}
                    />
                )}
                {headerFilters.category !== undefined && (
                    <ColoredTag
                        text={badgeConfig.title}
                        tagClicked={categoryTagClicked}
                    />
                )}
                {headerFilters.emi.min !== defaultEmiFilter.min && (
                    <ColoredTag
                        text={formatPriceForDisplay(
                            [
                                Math.max(
                                    headerFilters.emi.min,
                                    searchTerms.emiTerms.min
                                ),
                                Math.min(
                                    headerFilters.emi.max,
                                    searchTerms.emiTerms.max
                                ),
                            ],
                            lang,
                            t
                        )}
                        tagClicked={emiTagClicked}
                    />
                )}
                {Object.keys(headerFilters.makeModel).map(make => {
                    return Object.keys(headerFilters.makeModel[make].models)
                        .length ===
                        searchTerms.makeModelTree[make].modelsSlug.length ? (
                        <ColoredTag
                            key={make}
                            text={formatBrandName(
                                searchTerms.makeModelTree[make].make
                            )}
                            tagClicked={makeTagClicked}
                        />
                    ) : (
                        Object.keys(headerFilters.makeModel[make].models).map(
                            model => (
                                <span
                                    onClick={() =>
                                        modelTagClicked({
                                            make: make,
                                            model,
                                        })
                                    }
                                    key={model}
                                >
                                    <ColoredTag
                                        text={formatBrandName(
                                            searchTerms.makeModelTree[make]
                                                .models[model]
                                        )}
                                        tagClicked={() => null}
                                    />
                                </span>
                            )
                        )
                    );
                })}

                {Object.keys(headerFilters.bodystyle).length !== 0 &&
                    Object.keys(headerFilters.bodystyle).map(item => {
                        return (
                            <ColoredTag
                                key={item}
                                text={
                                    searchTerms.bodyStyleTerms.bodyStyles[item]
                                }
                                tagClicked={bodyTypeTagClicked}
                            />
                        );
                    })}
                {Object.keys(headerFilters.transmission).length !== 0 &&
                    Object.keys(headerFilters.transmission).map(item => (
                        <ColoredTag
                            key={item}
                            text={item}
                            tagClicked={transmissionTagClicked}
                        />
                    ))}

                {headerFilters.kilometers && headerFilters.kilometers !== 0 && (
                    <ColoredTag
                        text={`${numberWithCommas(
                            headerFilters.kilometers
                        )} ${t('km')} ${t('& Less')}`}
                        tagClicked={kilometersTagClicked}
                    />
                )}
                {headerFilters.status && (
                    <ColoredTag
                        text={t(CarStatusText[headerFilters.status]) || ''}
                        tagClicked={availableTagClicked}
                    />
                )}
                {headerFilters.year.min != defaultYearFilter.min && (
                    <ColoredTag
                        text={formatYearForDisplay(
                            headerFilters.year.max !== defaultYearFilter.max
                                ? [
                                      headerFilters.year.min,
                                      headerFilters.year.max,
                                  ]
                                : [headerFilters.year.min]
                        )}
                        tagClicked={yearTagClicked}
                    />
                )}
            </div>
            {openSort && <SortMobile open={openSort} setOpen={setOpenSort} />}
            {open && (
                <FiltersMobile
                    applyFiltersClicked={applyFiltersClicked}
                    clearFiltersClicked={clearFiltersClicked}
                    open={open}
                    setOpen={setOpen}
                    filterSelected={selectedFilter}
                    localFilters={{ ...filters }}
                    displayedCount={displayedCount}
                    searchTerms={searchTerms}
                />
            )}
        </>
    );
};

export default FilterHeaderMobile;
